import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import { api } from '@/api/client';
import { extractErrorMessage } from '@/api/errors';
import {
  ConsultationBillingStatus,
  ConsultationListItem,
  ConsultationStatus,
  ConsultationsListResponse,
} from '@/types';

const STATUS_OPTIONS: { value: ConsultationStatus | ''; label: string }[] = [
  { value: '', label: 'Any status' },
  { value: 'pending', label: 'Pending' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'no_show', label: 'No-show' },
  { value: 'rescheduled', label: 'Rescheduled' },
  { value: 'awaiting_payment', label: 'Awaiting payment' },
  { value: 'awaiting_consultant', label: 'Awaiting consultant' },
];

const BILLING_OPTIONS: {
  value: ConsultationBillingStatus | '';
  label: string;
}[] = [
  { value: '', label: 'Any billing' },
  { value: 'pending_payment', label: 'Pending payment' },
  { value: 'paid', label: 'Paid' },
  { value: 'refunded', label: 'Refunded' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'expired', label: 'Expired' },
];

const STATUS_TONE: Record<ConsultationStatus, string> = {
  booking_requested: 'bg-slate-100 text-slate-700',
  awaiting_consultant: 'bg-amber-100 text-amber-700',
  awaiting_payment: 'bg-amber-100 text-amber-700',
  denied: 'bg-red-100 text-red-700',
  referred: 'bg-violet-100 text-violet-700',
  pending_reschedule_confirmation: 'bg-amber-100 text-amber-700',
  pending: 'bg-amber-100 text-amber-700',
  confirmed: 'bg-blue-100 text-blue-700',
  in_progress: 'bg-indigo-100 text-indigo-700',
  completed: 'bg-emerald-100 text-emerald-700',
  cancelled: 'bg-slate-200 text-slate-700',
  no_show: 'bg-red-100 text-red-700',
  rescheduled: 'bg-violet-100 text-violet-700',
};

const BILLING_TONE: Record<ConsultationBillingStatus, string> = {
  not_requested: 'bg-slate-100 text-slate-600',
  pending_payment: 'bg-amber-100 text-amber-700',
  paid: 'bg-emerald-100 text-emerald-700',
  expired: 'bg-slate-200 text-slate-700',
  cancelled: 'bg-slate-200 text-slate-700',
  refunded: 'bg-red-100 text-red-700',
};

function formatNaira(value: string | null) {
  const n = Number(value ?? 0);
  if (!Number.isFinite(n) || !value) return '—';
  return new Intl.NumberFormat('en-NG', {
    style: 'currency',
    currency: 'NGN',
    maximumFractionDigits: 0,
  }).format(n);
}

function formatDate(iso: string | null | undefined) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

export function ConsultationsListPage() {
  const [page, setPage] = useState(1);
  const [perPage] = useState(20);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [statusFilter, setStatusFilter] = useState<ConsultationStatus | ''>('');
  const [billingFilter, setBillingFilter] =
    useState<ConsultationBillingStatus | ''>('');
  const [data, setData] = useState<ConsultationsListResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const params = useMemo(
    () => ({
      page,
      per_page: perPage,
      ...(search && { search }),
      ...(statusFilter && { status: statusFilter }),
      ...(billingFilter && { billing_status: billingFilter }),
    }),
    [page, perPage, search, statusFilter, billingFilter]
  );

  const fetchConsultations = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<ConsultationsListResponse>(
        '/back-office/consultations',
        { params }
      );
      setData(res.data);
    } catch (err: unknown) {
      setError(extractErrorMessage(err, 'Failed to load consultations'));
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => {
    fetchConsultations();
  }, [fetchConsultations]);

  const onSearchSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setPage(1);
    setSearch(searchInput.trim());
  };

  return (
    <div className="px-8 py-6">
      <div>
        <h1 className="text-2xl font-semibold text-slate-800">Consultations</h1>
        <p className="text-sm text-slate-500">
          Appointments between patients and consultants. Settlement support is
          coming in a follow-up — view-only for now.
        </p>
      </div>

      <form
        onSubmit={onSearchSubmit}
        className="mt-6 flex flex-wrap items-end gap-3 rounded border border-slate-200 bg-white p-4"
      >
        <div className="flex-1 min-w-[220px]">
          <label className="block text-xs font-medium text-slate-600">
            Search
          </label>
          <input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Reference, patient or consultant"
            className="mt-1 w-full rounded border border-slate-300 px-3 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600">
            Status
          </label>
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value as ConsultationStatus | '');
              setPage(1);
            }}
            className="mt-1 rounded border border-slate-300 px-3 py-1.5 text-sm"
          >
            {STATUS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600">
            Billing
          </label>
          <select
            value={billingFilter}
            onChange={(e) => {
              setBillingFilter(
                e.target.value as ConsultationBillingStatus | ''
              );
              setPage(1);
            }}
            className="mt-1 rounded border border-slate-300 px-3 py-1.5 text-sm"
          >
            {BILLING_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
        <button
          type="submit"
          className="rounded bg-brand-500 px-4 py-1.5 text-sm font-medium text-white hover:bg-brand-600"
        >
          Apply
        </button>
      </form>

      <div className="mt-6 overflow-hidden rounded border border-slate-200 bg-white">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-3">Reference</th>
              <th className="px-4 py-3">Patient</th>
              <th className="px-4 py-3">Consultant</th>
              <th className="px-4 py-3">Scheduled</th>
              <th className="px-4 py-3">Fee</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Billing</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading && (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-slate-500">
                  Loading…
                </td>
              </tr>
            )}
            {!loading && error && (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-red-600">
                  {error}
                </td>
              </tr>
            )}
            {!loading && !error && data?.data.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-slate-500">
                  No consultations match these filters.
                </td>
              </tr>
            )}
            {!loading &&
              !error &&
              data?.data.map((c) => <ConsultationRow key={c.id} c={c} />)}
          </tbody>
        </table>
      </div>

      {data && data.pagination.total > 0 && (
        <Pagination
          page={data.pagination.page}
          totalPages={data.pagination.total_pages}
          total={data.pagination.total}
          onChange={setPage}
        />
      )}
    </div>
  );
}

function ConsultationRow({ c }: { c: ConsultationListItem }) {
  return (
    <tr className="hover:bg-slate-50">
      <td className="px-4 py-3">
        <Link
          to={`/consultations/${c.id}`}
          className="font-mono text-xs font-medium text-brand-600 hover:underline"
        >
          {c.payment_reference ?? `#${c.id}`}
        </Link>
        <div className="mt-0.5 text-[11px] text-slate-400 capitalize">
          {c.consultation_type}
        </div>
      </td>
      <td className="px-4 py-3">
        {c.patient ? (
          <div>
            <div className="text-slate-800">
              {c.patient.first_name} {c.patient.last_name}
            </div>
            <div className="text-xs text-slate-500">{c.patient.email}</div>
          </div>
        ) : (
          <span className="text-slate-400">—</span>
        )}
      </td>
      <td className="px-4 py-3">
        {c.consultant ? (
          <div>
            <div className="text-slate-800">
              {c.consultant.first_name} {c.consultant.last_name}
            </div>
            <div className="text-xs text-slate-500">{c.consultant.email}</div>
          </div>
        ) : (
          <span className="text-slate-400">—</span>
        )}
      </td>
      <td className="px-4 py-3 text-slate-600 text-xs">
        {formatDate(c.scheduled_start_time)}
      </td>
      <td className="px-4 py-3 text-slate-700">
        {formatNaira(c.consultation_fee)}
      </td>
      <td className="px-4 py-3">
        <span
          className={clsx(
            'inline-flex rounded-full px-2 py-0.5 text-xs font-medium',
            STATUS_TONE[c.status]
          )}
        >
          {c.status.replace(/_/g, ' ')}
        </span>
      </td>
      <td className="px-4 py-3">
        <span
          className={clsx(
            'inline-flex rounded-full px-2 py-0.5 text-xs font-medium',
            BILLING_TONE[c.billing_status]
          )}
        >
          {c.billing_status.replace(/_/g, ' ')}
        </span>
      </td>
    </tr>
  );
}

function Pagination({
  page,
  totalPages,
  total,
  onChange,
}: {
  page: number;
  totalPages: number;
  total: number;
  onChange: (p: number) => void;
}) {
  return (
    <div className="mt-4 flex items-center justify-between text-sm text-slate-600">
      <div>
        Page {page} of {totalPages} · {total} total
      </div>
      <div className="flex gap-2">
        <button
          type="button"
          disabled={page <= 1}
          onClick={() => onChange(page - 1)}
          className="rounded border border-slate-300 px-3 py-1 disabled:opacity-50"
        >
          Prev
        </button>
        <button
          type="button"
          disabled={page >= totalPages}
          onClick={() => onChange(page + 1)}
          className="rounded border border-slate-300 px-3 py-1 disabled:opacity-50"
        >
          Next
        </button>
      </div>
    </div>
  );
}
